import { GetServerSideProps } from 'next'
import strapi from 'services/strapi'
import BlogPost, {
  BlogPostBook,
  BlogPostComponent,
  BlogPostText,
} from 'models/blog-post'
import Layout from 'components/layout'
import Avatar from 'components/avatar'
import Markdown from 'components/markdown'
import api from 'services/api'
import bluebird from 'bluebird'
import BookDetails from 'models/book-details'
import Cover from 'components/cover'
import Link from 'next/link'
import routes from 'routes'
import DateView from 'components/date-view'
import { NextSeo } from 'next-seo'
import { CANONICAL_PREFIX } from 'config'
import Breadcrumb from 'components/breadcrumb'
import { Heading } from 'pages'
import TagList from 'components/tag-list'
import AppDownload from 'components/app-download'
import Tag from 'models/tag'

export const BlogPostPage = ({
  post,
  books,
  path,
  tags,
}: {
  post: BlogPost
  books: BookDetails[]
  path: string
  tags: Tag[]
}): JSX.Element => {
  const titleImageUrl =
    post.title_image?.formats?.large?.url ??
    post.title_image?.formats?.medium?.url ??
    post.title_image?.formats?.small?.url

  return (
    <Layout showDownloadBtn={true}>
      <NextSeo
        title={post.title}
        description={post.meta_description}
        canonical={`${CANONICAL_PREFIX}${path}`}
        openGraph={{
          title: post.title,
          url:
            'https://m.anystories.app' + routes.blog.post({ slug: post.slug }),
          type: 'article',
          images: [
            {
              url: post.title_image?.url,
            },
          ],
        }}
      />
      <section className="desktop:flex">
        <section className="desktop:w-[1018px] mb-4">
          <div className="hidden lg:block px-4 py-3">
            <Breadcrumb
              className="mr-4"
              items={[
                {
                  name: 'Blog',
                  link: '/blog',
                },
                {
                  name: post.title,
                },
              ]}
            />
          </div>
          <article className="p-4 pb-16 font-body">
            <h1 className="text-3xl font-bold mb-2">{post.title}</h1>
            <div className="py-2 text-sm font-sans flex">
              <Avatar
                src={post.editor.avatar.formats.thumbnail.url}
                name={post.editor.name}
                className="w-5 h-5"
              />
              <span className="pl-1 font-medium">{post.editor.name}</span>
              <span className="text-gray-400 px-2"> | </span>
              <span className=" text-gray-400">
                Updated on <DateView date={post.updatedAt} />
              </span>
            </div>

            {titleImageUrl && (
              <img
                src={titleImageUrl}
                className="my-4 rounded-lg w-full"
                alt={post.title}
              />
            )}

            {post.introduction && (
              <Markdown
                markdown={post.introduction}
                className="mb-8 prose-lg"
              />
            )}

            <>
              {post.content.map((block) => (
                <BodyBlock block={block} key={block.id} books={books} />
              ))}
            </>
          </article>
        </section>
        <section className="desktop:w-[422px]">
          <section className="px-4 mb-[40px]">
            <Heading title="Tags" moreLink={routes.tag.list()} />
            <TagList tags={tags} />
          </section>
          <section className="px-4 mb-[40px]">
            <AppDownload />
          </section>
        </section>
      </section>
    </Layout>
  )
}

const BodyBlock = ({
  block,
  books,
}: {
  block: BlogPostComponent
  books: BookDetails[]
}): JSX.Element => {
  let blockView: JSX.Element

  switch (block.__component) {
    case 'blog-post.book':
      blockView = BodyBook({ block: block as unknown as BlogPostBook, books })
      break
    case 'blog-post.text':
      blockView = BodyText({ block: block as unknown as BlogPostText })
      break
  }

  return (
    <>
      {/* <hr className="border-gray-300 my-8" /> */}
      <section className="my-8 clear-both">{blockView}</section>
    </>
  )
}

const BodyBook = ({
  block,
  books,
}: {
  block: BlogPostBook
  books: BookDetails[]
}): JSX.Element => {
  const book = books
    .filter((book) => !!book)
    .find((book) => book.book.bookId === block.book_id)

  if (!book) return <></>

  const url = routes.story.details({
    id: book.book.bookId,
    title: book.book.title,
  })

  return (
    <>
      <h3 className="text-2xl font-bold underline">
        <Link href={url}>{block.book_title || book.book.title}</Link>
      </h3>
      <div className="text-lg italic text-gray-500">
        By {book.book.author.authorName}
      </div>
      <div className="pb-4 pt-3">
        <Cover
          book={book.book}
          className="float-right w-24 shadow-lg ml-4 mb-4"
        />
        <Markdown
          markdown={
            block.description?.trim().length > 0
              ? block.description
              : book.book.description
          }
          className="prose-lg"
        />
      </div>

      <div className="font-bold mb-16">
        <Link href={url} className="text-lg text-goldenrod underline">
          Read Now
        </Link>
      </div>
    </>
  )
}

const BodyText = ({ block }: { block: BlogPostText }): JSX.Element => {
  return <Markdown markdown={block.text} className="prose-lg" />
}

// export async function getStaticPaths() {
//   return {
//     paths: [{ params: { slug: 'most-popular-erotic-romance-novels' } }],
//     fallback: false,
//   }
// }

// export const getStaticProps: GetStaticProps = async ({ params }) => {
//   const slug = params.slug as string
//   const post = await strapi.blogPost.getOne(slug)

//   if (!post) return { notFound: true }

//   const bookIds = post.content
//     .filter((c) => c.__component === 'blog-post.book')
//     .map((c) => ((c as unknown) as BlogPostBook).book_id)

//   const books = await bluebird.map(
//     bookIds,
//     (id: string) => api.book.getBookInfo(id),
//     { concurrency: 50 }
//   )

//   return {
//     props: {
//       post,
//       books,
//       path: routes.blog.post({ slug }),
//     },
//   }
// }

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const slug = params.slug as string
  const post = await strapi.blogPost.getOne(slug)

  if (!post) return { notFound: true }

  const bookIds = post.content
    .filter((c) => c.__component === 'blog-post.book')
    .map((c) => (c as unknown as BlogPostBook).book_id)

  const books = await bluebird.map(
    bookIds,
    (id: string) => api.book.getBookInfo(id),
    { concurrency: 50 }
  )

  const tags = await api.tag.getHomeTags()
  return {
    props: {
      post,
      books,
      path: routes.blog.post({ slug }),
      tags,
    },
  }
}

export default BlogPostPage
